import Navigation from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { Star, Trash2, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { soundManager } from "@/lib/soundManager";

export default function SavedNumbers() {
  const { isAuthenticated } = useAuth();
  const [number, setNumber] = useState("");
  const [digitType, setDigitType] = useState<number>(4);

  const utils = trpc.useUtils();
  const { data: savedNumbers, isLoading } = trpc.savedNumbers.list.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  const addMutation = trpc.savedNumbers.add.useMutation({
    onSuccess: () => {
      toast.success("Number saved successfully!");
      setNumber("");
      utils.savedNumbers.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save number");
    },
  });

  const removeMutation = trpc.savedNumbers.remove.useMutation({
    onSuccess: () => {
      toast.success("Number removed");
      utils.savedNumbers.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to remove number");
    },
  });

  const handleAdd = () => {
    if (number.length !== digitType) {
      toast.error(`Number must be exactly ${digitType} digits`);
      return;
    }
    if (!/^\d+$/.test(number)) {
      toast.error("Number must contain only digits");
      return;
    }
    addMutation.mutate({ number, digitType });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
        <Navigation />
        <main className="flex-1 flex items-center justify-center pb-20 md:pb-8">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Login Required</h2>
              <p className="text-muted-foreground mb-4">
                Please login to save your favorite numbers
              </p>
              <Button asChild>
                <a href={getLoginUrl()}>Login</a>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
      <Navigation />
      <main className="flex-1 pb-20 md:pb-8">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Add Number Card */}
            <Card className="bg-gradient-to-br from-card to-blue-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  Add Favorite Number
                </CardTitle>
                <CardDescription>Save numbers you want to track</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Number Type</Label>
                  <RadioGroup
                    value={digitType.toString()}
                    onValueChange={(value) => {
                      soundManager.playClick();
                      setDigitType(parseInt(value));
                      setNumber("");
                    }}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="2" id="add-2" />
                      <Label htmlFor="add-2">2 Digits</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="3" id="add-3" />
                      <Label htmlFor="add-3">3 Digits</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="4" id="add-4" />
                      <Label htmlFor="add-4">4 Digits</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="number">Number</Label>
                  <div className="flex gap-2">
                    <Input
                      id="number"
                      type="text"
                      placeholder={`Enter ${digitType}-digit number`}
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      maxLength={digitType}
                      className="font-mono text-lg"
                    />
                    <Button 
                      onClick={() => {
                        soundManager.playClick();
                        handleAdd();
                      }} 
                      disabled={addMutation.isPending}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Saved Numbers List */}
            <Card className="bg-gradient-to-br from-card to-blue-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary" />
                  Your Saved Numbers
                </CardTitle>
                <CardDescription>Numbers you're tracking</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : savedNumbers && savedNumbers.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {savedNumbers.map((saved) => (
                      <div
                        key={saved.id}
                        className="relative p-4 rounded-lg bg-gradient-to-br from-primary/20 to-amber-500/20 border border-primary/30"
                      >
                        <div className="text-center mb-3">
                          <div className="text-2xl font-bold text-primary font-mono">{saved.number}</div>
                          <div className="text-xs text-muted-foreground">{saved.digitType} digits</div>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="w-full"
                          onClick={() => {
                            soundManager.playClick();
                            removeMutation.mutate({ id: saved.id });
                          }}
                          disabled={removeMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No saved numbers yet. Add your first favorite number above!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
